/**
 * Hook: Source file upload, connector import, project-level source management,
 * and schema warning tracking.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import type { UploadResult } from '../AddSourceDialog';
import { useQuery, useQueryClient } from 'react-query';
import type { WorkspaceOperation } from '../../../services/workspaceApi';
import { importFromConnector } from '../../../services/connectorApi';
import type { ConnectorConfig } from '../../../services/connectorApi';
import type { SchemaWarning } from '../DAGNode';
import {
  listProjectSources,
  removeProjectSource,
  triggerRemap,
} from '../../../services/projectSourceApi';
import type { ProjectSource } from '../../../services/projectSourceApi';

interface UseSourceManagementArgs {
  sessionId: string;
  /** Project ID — required for project-level source operations. */
  projectId?: string;
  /** Batch ID — scopes connector sources to this batch. */
  batchId?: string;
  operations: WorkspaceOperation[];
  sendMessage: { mutate: (msg: string | { message: string; displayText?: string }) => void };
}

function loadWarningsFromStorage(sessionId: string): Map<string, SchemaWarning> {
  try {
    const raw = sessionStorage.getItem(`schema_warnings_${sessionId}`);
    if (!raw) return new Map();
    const entries: Array<[string, SchemaWarning]> = JSON.parse(raw);
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function saveWarningsToStorage(sessionId: string, warnings: Map<string, SchemaWarning>) {
  try {
    sessionStorage.setItem(`schema_warnings_${sessionId}`, JSON.stringify(Array.from(warnings.entries())));
  } catch {
    // sessionStorage unavailable — warnings won't persist across tab switches
  }
}

function loadBaselineFromStorage(sessionId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(`schema_warnings_baseline_${sessionId}`);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveBaselineToStorage(sessionId: string, baseline: Set<string>) {
  try {
    sessionStorage.setItem(`schema_warnings_baseline_${sessionId}`, JSON.stringify(Array.from(baseline)));
  } catch {
    // ignore
  }
}

export function useSourceManagement({ sessionId, projectId, batchId, operations, sendMessage }: UseSourceManagementArgs) {
  const queryClient = useQueryClient();
  const [schemaWarnings, setSchemaWarningsState] = useState<Map<string, SchemaWarning>>(
    () => loadWarningsFromStorage(sessionId),
  );

  const setSchemaWarnings = useCallback((warnings: Map<string, SchemaWarning>) => {
    setSchemaWarningsState(warnings);
    saveWarningsToStorage(sessionId, warnings);
  }, [sessionId]);

  // Tracks op IDs that were already 'executed' at the time their warning was set.
  // A warning is only cleared when the op executes AGAIN after the warning — i.e.
  // when it was NOT in this set (meaning it was pending/running when warned) OR
  // when it transitions out of executed and back into executed.
  // Persisted to sessionStorage so page refresh doesn't lose the baseline.
  const warningBaselineRef = useRef<Set<string>>(loadBaselineFromStorage(sessionId));

  const [sourceConnectorPickerOpen, setSourceConnectorPickerOpen] = useState(false);
  const [addSourceDialogOpen, setAddSourceDialogOpen] = useState(false);
  const [addSourceDialogInitialTab, setAddSourceDialogInitialTab] = useState<0 | 1>(0);

  // Surface a prominent banner when source data is replaced with schema changes
  const [sourceReplacementAlert, setSourceReplacementAlert] = useState<{
    tableName: string;
    brokenCount: number;
    affectedCount: number;
    brokenOperationIds: string[];
    affectedOperationIds: string[];
  } | null>(null);

  // Clear schema warnings only when the warned operation is re-executed AFTER
  // the warning was set. Ops that were already 'executed' when the warning was
  // added are tracked in warningBaselineRef — they must first leave 'executed'
  // (go to 'pending'/'running') and come back, or simply not be in the baseline.
  const prevOpStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (schemaWarnings.size === 0) return;
    const newWarnings = new Map(schemaWarnings);
    let changed = false;
    schemaWarnings.forEach((_warning, opId) => {
      const op = operations.find((o) => o.operation_id === opId);
      if (!op) return;
      const prevStatus = prevOpStatusRef.current.get(opId);
      const isReExecuted =
        (op.status === 'executed' || op.status === 'discarded') &&
        // Was already executed when warning was set → only clear if it
        // transitioned away from executed first (prevStatus shows it moved)
        (!warningBaselineRef.current.has(opId) || (prevStatus !== 'executed' && prevStatus !== undefined));
      if (isReExecuted) {
        newWarnings.delete(opId);
        warningBaselineRef.current.delete(opId);
        changed = true;
      }
    });
    if (changed) saveBaselineToStorage(sessionId, warningBaselineRef.current);
    // Update previous status snapshot
    operations.forEach((op) => {
      prevOpStatusRef.current.set(op.operation_id, op.status);
    });
    if (changed) setSchemaWarnings(newWarnings);
  }, [operations, schemaWarnings]);

  // Auto-clear replacement alert when all affected operations are re-executed
  useEffect(() => {
    if (!sourceReplacementAlert) return;
    const allIds = [
      ...sourceReplacementAlert.brokenOperationIds,
      ...sourceReplacementAlert.affectedOperationIds,
    ];
    const allResolved = allIds.every((id) => {
      const op = operations.find((o) => o.operation_id === id);
      return !op || op.status === 'executed' || op.status === 'discarded';
    });
    if (allResolved) setSourceReplacementAlert(null);
  }, [operations, sourceReplacementAlert]);

  const [replaceTableName, setReplaceTableName] = useState<string | null>(null);
  const [replaceTableGroup, setReplaceTableGroup] = useState<string[]>([]);

  const handleReplaceSource = useCallback(
    (tableName: string, groupTableNames: string[] = []) => {
      setReplaceTableName(tableName);
      setReplaceTableGroup(groupTableNames);
      setAddSourceDialogOpen(true);
    },
    [],
  );

  const handleAddFromConnector = useCallback(() => {
    setSourceConnectorPickerOpen(true);
  }, []);

  const handleSourceConnectorSelect = useCallback(
    async (connector: ConnectorConfig, tablesStr: string, columnsByTable?: Record<string, string[]>) => {
      setSourceConnectorPickerOpen(false);
      if (!sessionId || !tablesStr) return;
      const tables = tablesStr.split(',').map((t) => t.trim()).filter(Boolean);
      try {
        const result = await importFromConnector(sessionId, connector.connector_id, tables, columnsByTable);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
        queryClient.invalidateQueries(['projectSources', projectId, batchId]);
        queryClient.invalidateQueries(['projectSources', projectId]);
        const tableList = result.tables
          .map((t) => `"${t.table_name}" (${t.row_count} rows)`)
          .join(', ');
        sendMessage.mutate(
          `I imported ${result.tables_imported} table(s) from connector "${connector.name}": ${tableList}. They are now in the workspace as source tables.`,
        );
      } catch (err: any) {
        console.error('Connector import error:', err);
        sendMessage.mutate(
          `Failed to import from connector "${connector.name}": ${err.message || err}`,
        );
      }
    },
    [sessionId, projectId, batchId, sendMessage, queryClient],
  );

  // ── Project-level source management ──────────────────────────────────

  // Fetch project sources with auto-polling while any source is profiling
  const projectSourcesQuery = useQuery(
    ['projectSources', projectId, batchId],
    () => listProjectSources(projectId!, batchId),
    {
      enabled: !!projectId,
      staleTime: 10_000,
      refetchInterval: (data) => {
        if (!data) return false;
        return data.some((s: ProjectSource) => s.status === 'profiling' || s.status === 'pending')
          ? 5_000
          : false;
      },
    },
  );

  const projectSources = projectSourcesQuery.data ?? [];

  // "New sources profiled" alert — reuses the sourceReplacementAlert pattern
  const [newSourcesProfiledAlert, setNewSourcesProfiledAlert] = useState<{
    sourceLabels: string[];
    sourceIds: string[];
  } | null>(null);

  // Track previously-seen ready source IDs to detect newly profiled/active sources.
  // Sources transition pending → profiling → profiled → active, so we detect
  // any source reaching 'profiled' or 'active' that wasn't in the ready set before.
  const prevReadyIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const readyStatuses = new Set(['profiled', 'active']);
    const currentReady = new Set(
      projectSources.filter((s) => readyStatuses.has(s.status)).map((s) => s.source_id),
    );
    const newlyReady = projectSources.filter(
      (s) => readyStatuses.has(s.status) && !prevReadyIdsRef.current.has(s.source_id),
    );
    if (newlyReady.length > 0 && prevReadyIdsRef.current.size > 0) {
      setNewSourcesProfiledAlert({
        sourceLabels: newlyReady.map((s) => s.label ?? s.source_id.slice(0, 8)),
        sourceIds: newlyReady.map((s) => s.source_id),
      });
    }
    prevReadyIdsRef.current = currentReady;
  }, [projectSources]);

  const handleOpenAddSourceDialog = useCallback((tab?: 0 | 1) => {
    setAddSourceDialogInitialTab(tab ?? 0);
    setAddSourceDialogOpen(true);
  }, []);
  const handleOpenAddSourceDialogFiles = useCallback(() => handleOpenAddSourceDialog(0), [handleOpenAddSourceDialog]);
  const handleOpenAddSourceDialogConnector = useCallback(() => handleOpenAddSourceDialog(1), [handleOpenAddSourceDialog]);

  const handleUploadsComplete = useCallback((results: UploadResult[]) => {
    for (const data of results) {
      if (data.replaced) {
        const diff = data.schemaDiff || {};
        const parts: string[] = [
          `I replaced the "${data.tableName}" table (${data.rowCount} rows).`,
        ];
        if (diff.removed_columns?.length) {
          parts.push(`⚠️ Removed columns: ${diff.removed_columns.join(', ')}`);
        }
        if (diff.added_columns?.length) {
          parts.push(`New columns: ${diff.added_columns.join(', ')}`);
        }
        if (diff.type_changed?.length) {
          parts.push(`⚠️ Type changed: ${diff.type_changed.map((tc) => `${tc.column} (${tc.old_type} → ${tc.new_type})`).join(', ')}`);
        }
        if (diff.possible_renames?.length) {
          parts.push(`💡 Possible renames: ${diff.possible_renames.map((r) => `"${r.old}" → "${r.new}"`).join(', ')}`);
        }
        if (data.affectedOperations?.length) {
          parts.push(`⚠️ ${data.affectedOperations.length} downstream transform(s) may be affected.`);
        }
        if (data.brokenOperations?.length) {
          parts.push(`🔴 ${data.brokenOperations.length} transform(s) reference removed columns and will break.`);
        }
        sendMessage.mutate(parts.join('\n'));

        const warnings = new Map<string, SchemaWarning>(schemaWarnings);
        for (const op of data.brokenOperations || []) {
          warnings.set(op.operation_id, {
            operationId: op.operation_id,
            severity: 'error',
            message: `Broken: column${op.broken_columns.length > 1 ? 's' : ''} ${op.broken_columns.join(', ')} no longer exist in "${data.tableName}"`,
            brokenColumns: op.broken_columns,
          });
        }
        for (const op of data.affectedOperations || []) {
          if (!warnings.has(op.operation_id)) {
            warnings.set(op.operation_id, {
              operationId: op.operation_id,
              severity: 'warning',
              message: `Column types changed in "${data.tableName}" — review this transform`,
            });
          }
        }
        // Record which of the warned ops are already 'executed' right now so the
        // clear effect knows not to remove them until they are re-executed.
        const newBaseline = new Set(warningBaselineRef.current);
        Array.from(warnings.keys()).forEach((opId) => {
          const op = operations.find((o) => o.operation_id === opId);
          if (op && (op.status === 'executed' || op.status === 'discarded')) {
            newBaseline.add(opId);
          }
        });
        warningBaselineRef.current = newBaseline;
        saveBaselineToStorage(sessionId, newBaseline);
        setSchemaWarnings(warnings);

        const brokenOps = (data.brokenOperations || []).map((o) => o.operation_id);
        const affectedOps = (data.affectedOperations || []).map((o) => o.operation_id);
        if (brokenOps.length > 0 || affectedOps.length > 0) {
          setSourceReplacementAlert({
            tableName: data.tableName,
            brokenCount: brokenOps.length,
            affectedCount: affectedOps.length,
            brokenOperationIds: brokenOps,
            affectedOperationIds: affectedOps,
          });
        }
      } else {
        sendMessage.mutate(
          `I just uploaded a reference table "${data.tableName}" (${data.rowCount} rows, ${data.columnCount} columns). It's now in the workspace.`,
        );
      }
    }
  }, [sendMessage, schemaWarnings, operations, setSchemaWarnings, setSourceReplacementAlert]);

  const handleProjectSourceAdded = useCallback(() => {
    queryClient.invalidateQueries(['agent-workspace', sessionId]);
    queryClient.invalidateQueries(['projectSources', projectId, batchId]);
    queryClient.invalidateQueries(['projectSources', projectId]);
    setTimeout(() => {
      queryClient.invalidateQueries(['agent-workspace', sessionId]);
    }, 1500);
  }, [queryClient, sessionId, projectId, batchId]);

  const handleRemoveProjectSource = useCallback(
    async (sourceId: string) => {
      if (!projectId) return;
      await removeProjectSource(projectId, sourceId);
      queryClient.invalidateQueries(['projectSources', projectId]);
    },
    [projectId, queryClient],
  );

  const handleTriggerRemap = useCallback(async () => {
    if (!projectId) return;
    try {
      await triggerRemap(projectId);
      setNewSourcesProfiledAlert(null);
    } catch (err: any) {
      console.error('Failed to trigger remap:', err);
      sendMessage.mutate(
        `Failed to trigger re-mapping: ${err.message || err}`,
      );
    }
  }, [projectId, sendMessage]);

  return {
    schemaWarnings,
    sourceConnectorPickerOpen,
    sourceReplacementAlert,
    dismissReplacementAlert: useCallback(() => setSourceReplacementAlert(null), []),
    handleReplaceSource,
    handleAddFromConnector,
    handleSourceConnectorSelect,
    closeSourceConnectorPicker: useCallback(() => setSourceConnectorPickerOpen(false), []),
    // Project-level source management
    projectSources,
    projectSourcesLoading: projectSourcesQuery.isLoading,
    addSourceDialogOpen,
    addSourceDialogInitialTab,
    handleOpenAddSourceDialog,
    handleOpenAddSourceDialogFiles,
    handleOpenAddSourceDialogConnector,
    handleCloseAddSourceDialog: useCallback(() => {
      setAddSourceDialogOpen(false);
      setReplaceTableName(null);
      setReplaceTableGroup([]);
    }, []),
    replaceTableName,
    replaceTableGroup,
    handleUploadsComplete,
    handleProjectSourceAdded,
    handleRemoveProjectSource,
    handleTriggerRemap,
    newSourcesProfiledAlert,
    dismissNewSourcesProfiledAlert: useCallback(() => setNewSourcesProfiledAlert(null), []),
  };
}
